/**
 * LiveActivityDetail — prominent dashboard cards for "Currently Travelling"
 * and "Currently On Machines" plus a detail modal that pops the receipts/logs
 * for a single row.
 *
 * Mirrors the visual style of the existing "Who's On Site Now" card but
 * built specifically for travel + machine usage data.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, Wrench, MapPin, User as UserIcon, Receipt, FileText,
  ChevronRight, ChevronDown, Phone, MessageCircle, ExternalLink,
  AlertTriangle, Clock, Fuel, Edit, X
} from "lucide-react";
import { Button, Card, Badge, Modal, Spinner } from "../shared/ui";
import EditClockedOnModal from "./EditClockedOnModal";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-AU", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
const fmtMoney = (v) => `$${(Number(v) || 0).toFixed(2)}`;

export const LiveActivityDetail = ({ liveStatus, onContact, refresh }) => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const [detail, setDetail] = useState(null); // { kind: 'travel'|'machine'|'hire', data, loading }
  const [editTarget, setEditTarget] = useState(null); // { kind, session }
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const open = async (kind, row) => {
    setDetail({ kind, row, data: null, loading: true });
    let url;
    if (kind === "travel") url = `${API}/dashboard/live-detail/travel/${row.travel_entry_id}`;
    else if (kind === "machine") url = `${API}/dashboard/live-detail/machine/${row.machine_usage_id}`;
    else url = `${API}/dashboard/live-detail/general-hire/${row.general_hire_id}`;
    try {
      const r = await axios.get(url);
      setDetail({ kind, row, data: r.data, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load detail");
      setDetail(null);
    }
  };

  const close = () => setDetail(null);

  const deleteRow = async (kind, row) => {
    const label = row.user_name || row.machine_name || "this entry";
    if (!window.confirm(`Delete ${label}? This wipes the record from the database. Only use for test/junk rows.`)) return;
    try {
      let url;
      if (kind === "travel") url = `${API}/travel/entries/${row.travel_entry_id}`;
      else if (kind === "machine") url = `${API}/machines/usage/${row.machine_usage_id}`;
      else url = `${API}/general-hires/${row.general_hire_id}`;
      await axios.delete(url);
      toast.success("Deleted");
      refresh && refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const travelling = (liveStatus?.rows || []).filter((r) => r.type === "travelling");
  const onMachine = (liveStatus?.rows || []).filter((r) => r.type === "on_machine");
  const generalHire = (liveStatus?.rows || []).filter((r) => r.type === "general_hire");

  return (
    <>
      {/* Currently Travelling */}
      <Card
        data-testid="live-travelling-section"
        className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold uppercase tracking-tight font-heading flex items-center gap-2">
            <Truck className="w-6 h-6 text-amber-600" />
            Currently Travelling
          </h3>
          <Badge variant="warning" className="text-lg px-4 py-1">
            {travelling.length} active
          </Badge>
        </div>
        {travelling.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">
            Nobody on the road right now.
          </p>
        ) : (
          <div className="space-y-2">
            {travelling.map((r, idx) => (
              <TravellingRow
                key={`t-${r.travel_entry_id || idx}`}
                row={r}
                onView={() => open("travel", r)}
                onEdit={() => setEditTarget({ kind: "travel", session: r })}
                onContact={() => onContact(r)}
                onDelete={isAdmin ? () => deleteRow("travel", r) : null}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Currently On Machines */}
      <Card
        data-testid="live-onmachine-section"
        className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold uppercase tracking-tight font-heading flex items-center gap-2">
            <Wrench className="w-6 h-6 text-purple-600" />
            Currently On Machines
          </h3>
          <Badge variant="info" className="text-lg px-4 py-1">
            {onMachine.length} active
          </Badge>
        </div>
        {onMachine.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">
            No machines in use right now.
          </p>
        ) : (
          <div className="space-y-2">
            {onMachine.map((r, idx) => (
              <MachineRow
                key={`m-${r.machine_usage_id || idx}`}
                row={r}
                onView={() => open("machine", r)}
                onEdit={() => setEditTarget({ kind: "machine", session: r })}
                onContact={() => onContact(r)}
                onDelete={isAdmin ? () => deleteRow("machine", r) : null}
              />
            ))}
          </div>
        )}
      </Card>

      {/* General Hire In Progress */}
      {generalHire.length > 0 && (
        <Card
          data-testid="live-generalhire-section"
          className="border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold uppercase tracking-tight font-heading flex items-center gap-2">
              <Truck className="w-6 h-6 text-rose-600" />
              General Hire In Progress
            </h3>
            <Badge variant="error" className="text-lg px-4 py-1">
              {generalHire.length} active
            </Badge>
          </div>
          <div className="space-y-2">
            {generalHire.map((r, idx) => (
              <MachineRow
                key={`gh-${r.general_hire_id || idx}`}
                row={r}
                onView={() => open("hire", r)}
                onEdit={() => setEditTarget({ kind: "hire", session: r })}
                onContact={() => onContact(r)}
                onDelete={isAdmin ? () => deleteRow("hire", r) : null}
                isHire
              />
            ))}
          </div>
        </Card>
      )}

      {/* Detail modal */}
      <DetailModal
        detail={detail}
        onClose={close}
        onRefresh={() => {
          refresh && refresh();
        }}
        onPreviewImage={(url) => setLightboxUrl(url)}
      />

      {/* Edit modal — admin can edit any active/completed session */}
      {editTarget && (
        <EditClockedOnModal
          kind={editTarget.kind}
          session={editTarget.session}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            refresh && refresh();
          }}
        />
      )}

      {/* Image lightbox — handles data: URLs and remote URLs that browsers refuse to open in new tabs */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxUrl(null)}
          data-testid="receipt-lightbox"
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white bg-black/40 hover:bg-black/60 rounded-full p-2"
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
          {/^data:application\/pdf|\.pdf$/i.test(lightboxUrl) ? (
            <iframe
              src={lightboxUrl}
              title="Receipt"
              className="w-full h-full max-w-5xl max-h-[90vh] bg-white rounded"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={lightboxUrl}
              alt="Receipt"
              className="max-w-full max-h-full object-contain rounded shadow-2xl bg-white"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.querySelector('[data-fallback]').style.display = 'flex'; }}
            />
          )}
          <div data-fallback className="hidden flex-col items-center text-white text-center gap-2" onClick={(e) => e.stopPropagation()}>
            <p className="text-lg font-bold">Receipt couldn't load</p>
            <p className="text-sm opacity-80">The file may have been deleted or never properly uploaded.</p>
          </div>
        </div>
      )}
    </>
  );
};

const TravellingRow = ({ row, onView, onEdit, onContact, onDelete }) => (
  <div
    className="bg-white rounded-lg border-2 border-slate-100 p-3 hover:border-amber-300 transition-colors"
    data-testid={`travelling-row-${row.travel_entry_id || row.user_id}`}
  >
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Truck className="w-5 h-5 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-slate-800">{row.user_name}</p>
            <Badge variant={row.travel_role === "driver" ? "info" : "default"}>
              {row.travel_role || "driver"}
            </Badge>
            {row.user_role && (
              <span className="text-xs text-slate-500 capitalize">
                {row.user_role.replace("_", " ")}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 mt-0.5">
            {row.machine_name && (
              <strong>
                {row.machine_name}
                {row.machine_rego ? ` (${row.machine_rego})` : ""}
              </strong>
            )}
            {row.starting_point && row.destination && (
              <>
                {row.machine_name ? " · " : ""}
                {row.starting_point} → {row.destination}
              </>
            )}
          </p>
          {row.passengers?.length > 0 && (
            <p className="text-xs text-slate-500">
              with {row.passengers.join(", ")}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-right">
          <p className="text-sm text-amber-600 font-medium">
            {row.hours_so_far != null ? `${row.hours_so_far}h on road` : "—"}
          </p>
          <p className="text-xs text-slate-400">since {fmtTime(row.started_at)}</p>
        </div>
        {row.fuel_stops_count > 0 && (
          <Badge variant="warning" className="flex items-center gap-1">
            <Fuel className="w-3 h-3" /> {row.fuel_stops_count}
          </Badge>
        )}
        {row.incidents && (
          <Badge variant="error" className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Incident
          </Badge>
        )}
        <Button size="sm" variant="outline" onClick={onView} data-testid={`view-travel-${row.travel_entry_id}`}>
          <ChevronRight className="w-4 h-4 mr-1" /> Details
        </Button>
        {onEdit && (
          <button
            onClick={onEdit}
            className="p-2 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700"
            title="Edit this travel session"
            data-testid={`edit-travel-${row.travel_entry_id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
        {onContact && (
          <button
            onClick={onContact}
            className="p-2 rounded-md bg-slate-100 hover:bg-orange-100 text-slate-600 hover:text-orange-600"
            title={`Contact ${row.user_name}`}
            data-testid={`contact-travel-${row.user_id}`}
          >
            <MessageCircle className="w-4 h-4" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="p-2 rounded-md bg-red-100 hover:bg-red-200 text-red-700"
            title="Delete this travel entry"
            data-testid={`delete-travel-${row.travel_entry_id}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  </div>
);

const MachineRow = ({ row, onView, onEdit, onContact, onDelete, isHire }) => (
  <div
    className={`bg-white rounded-lg border-2 border-slate-100 p-3 transition-colors ${
      isHire ? "hover:border-rose-300" : "hover:border-purple-300"
    }`}
    data-testid={`${isHire ? "hire" : "machine"}-row-${row.machine_usage_id || row.general_hire_id || row.user_id}`}
  >
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isHire ? "bg-rose-100" : "bg-purple-100"
          }`}
        >
          <Wrench className={`w-5 h-5 ${isHire ? "text-rose-600" : "text-purple-600"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-slate-800">{row.user_name}</p>
            {row.user_role && (
              <span className="text-xs text-slate-500 capitalize">
                {row.user_role.replace("_", " ")}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 mt-0.5">
            <strong>{row.machine_name || "Machine"}</strong>
            {row.machine_rego ? ` (${row.machine_rego})` : ""}
            {row.job_title && <> · {row.job_title}</>}
            {row.client_name && row.client_name !== row.job_title && (
              <> · {row.client_name}</>
            )}
          </p>
          {row.dogman_names?.length > 0 && (
            <p className="text-xs text-slate-500">
              with {row.dogman_names.join(", ")}
            </p>
          )}
          {row.job_location && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {row.job_location}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-right">
          <p className={`text-sm font-medium ${isHire ? "text-rose-600" : "text-purple-600"}`}>
            {row.hours_so_far != null ? `${row.hours_so_far}h running` : "—"}
          </p>
          <p className="text-xs text-slate-400">since {fmtTime(row.started_at)}</p>
        </div>
        {row.receipts_count > 0 && (
          <Badge variant="default" className="flex items-center gap-1" data-testid={`receipt-count-${row.machine_usage_id || row.general_hire_id}`}>
            <Receipt className="w-3 h-3" /> {row.receipts_count} · {fmtMoney(row.receipts_total)}
          </Badge>
        )}
        {row.daily_logs_count > 0 && (
          <Badge variant="info" className="flex items-center gap-1">
            <FileText className="w-3 h-3" /> {row.daily_logs_count}
          </Badge>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onView}
          data-testid={`view-${isHire ? "hire" : "machine"}-${row.machine_usage_id || row.general_hire_id}`}
        >
          <ChevronRight className="w-4 h-4 mr-1" /> Details
        </Button>
        {onEdit && (
          <button
            onClick={onEdit}
            className="p-2 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700"
            title={`Edit this ${isHire ? "hire" : "machine"} session`}
            data-testid={`edit-${isHire ? "hire" : "machine"}-${row.machine_usage_id || row.general_hire_id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
        {onContact && row.user_id && (
          <button
            onClick={onContact}
            className="p-2 rounded-md bg-slate-100 hover:bg-orange-100 text-slate-600 hover:text-orange-600"
            title={`Contact ${row.user_name}`}
            data-testid={`contact-machine-${row.user_id}`}
          >
            <MessageCircle className="w-4 h-4" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="p-2 rounded-md bg-red-100 hover:bg-red-200 text-red-700"
            title={`Delete this ${isHire ? "general hire" : "machine session"}`}
            data-testid={`delete-${isHire ? "hire" : "machine"}-${row.machine_usage_id || row.general_hire_id}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  </div>
);

const DetailModal = ({ detail, onClose, onPreviewImage }) => {
  if (!detail) return null;
  const { kind, row, data, loading } = detail;
  const title =
    kind === "travel"
      ? `Travel — ${row.user_name}`
      : kind === "machine"
      ? `Machine Usage — ${row.machine_name || ""}`
      : `General Hire — ${row.machine_name || ""}`;
  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      )}
      {!loading && data && kind === "travel" && <TravelDetail data={data} onPreviewImage={onPreviewImage} />}
      {!loading && data && kind === "machine" && <MachineDetail data={data} onPreviewImage={onPreviewImage} />}
      {!loading && data && kind === "hire" && <HireDetail data={data} onPreviewImage={onPreviewImage} />}
    </Modal>
  );
};

const TravelDetail = ({ data, onPreviewImage }) => {
  const fuelStops = data.fuel_stops || [];
  const totalCost = fuelStops.reduce((s, f) => s + (f.fuel_cost || 0), 0);
  const totalLitres = fuelStops.reduce((s, f) => s + (f.fuel_amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="travel-detail">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Driver" value={data.user_name} />
        <Stat label="Role" value={data.travel_role} />
        <Stat label="Started" value={fmtDateTime(data.clock_in)} />
        <Stat label="Status" value={data.status || "travelling"} />
      </div>
      <Card>
        <h4 className="text-sm font-bold uppercase text-slate-600 mb-2 flex items-center gap-1">
          <MapPin className="w-4 h-4" /> Route
        </h4>
        <p className="text-sm">
          <strong>{data.starting_point || "?"}</strong>
          {" → "}
          <strong>{data.destination || data.finishing_point || "?"}</strong>
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <Stat label="Vehicle" value={data.machine_name || data.vehicle_name || "—"} />
          <Stat label="Rego" value={data.machine_rego || data.vehicle_rego || "—"} />
          <Stat label="Starting KM" value={data.starting_km != null ? `${data.starting_km} km` : "—"} />
          <Stat label="Ending KM" value={data.ending_km != null ? `${data.ending_km} km` : "—"} />
        </div>
      </Card>

      {data.passengers?.length > 0 && (
        <Card>
          <h4 className="text-sm font-bold uppercase text-slate-600 mb-2 flex items-center gap-1">
            <UserIcon className="w-4 h-4" /> Passengers ({data.passengers.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {data.passengers.map((p, i) => (
              <Badge key={i} variant="default">
                {typeof p === "string" ? p : p.user_name || "Unknown"}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold uppercase text-slate-600 flex items-center gap-1">
            <Fuel className="w-4 h-4" /> Fuel Stops ({fuelStops.length})
          </h4>
          <span className="text-xs text-slate-500">
            {totalLitres.toFixed(1)}L · {fmtMoney(totalCost)}
          </span>
        </div>
        {fuelStops.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No fuel stops yet.</p>
        ) : (
          <div className="space-y-2">
            {fuelStops.map((fs, i) => (
              <div
                key={fs.id || i}
                className="flex items-start justify-between p-2 bg-slate-50 rounded border border-slate-200"
                data-testid={`fuel-stop-${fs.id || i}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm">
                    {fs.station || fs.vendor || "Fuel Stop"}
                  </p>
                  <p className="text-xs text-slate-600">
                    {fs.date ? fmtDateTime(fs.date) : ""}
                    {fs.fuel_type ? ` · ${fs.fuel_type}` : ""}
                    {fs.fuel_amount ? ` · ${fs.fuel_amount}L` : ""}
                    {fs.payment_type ? ` · ${fs.payment_type}` : ""}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold">{fmtMoney(fs.fuel_cost)}</p>
                  {fs.receipt_url && (
                    <button
                      type="button"
                      onClick={() => onPreviewImage && onPreviewImage(fs.receipt_url)}
                      className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                      data-testid={`fuel-receipt-view-${fs.id || i}`}
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {data.incidents && (
        <Card className="bg-red-50 border-red-200">
          <h4 className="text-sm font-bold uppercase text-red-700 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> Incidents
          </h4>
          <p className="text-sm">{data.incidents}</p>
        </Card>
      )}
      {data.notes && (
        <Card>
          <h4 className="text-sm font-bold uppercase text-slate-600 mb-2">Notes</h4>
          <p className="text-sm whitespace-pre-line">{data.notes}</p>
        </Card>
      )}
    </div>
  );
};

const MachineDetail = ({ data, onPreviewImage }) => {
  const receipts = data.daybook_receipts || [];
  const dailyLogs = data.daily_logs || [];
  const totalReceipts = receipts.reduce((s, r) => s + (r.amount || 0), 0);
  const totalLoggedHours = dailyLogs.reduce((s, l) => s + (l.hours || 0), 0);
  return (
    <div className="space-y-4" data-testid="machine-detail">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Operator" value={data.operator_name} />
        <Stat label="Started" value={fmtDateTime(data.clock_on_time || data.start_date)} />
        <Stat label="Status" value={data.status} />
        <Stat label="Rate" value={data.rate_type || "—"} />
      </div>
      <Card>
        <h4 className="text-sm font-bold uppercase text-slate-600 mb-2 flex items-center gap-1">
          <Wrench className="w-4 h-4" /> Machine
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Name" value={data.machine_name} />
          <Stat label="Rego" value={data.machine_full?.rego || "—"} />
          <Stat label="Start hrs" value={data.starting_hours != null ? `${data.starting_hours}h` : "—"} />
          <Stat label="Start km" value={data.starting_kms != null ? `${data.starting_kms} km` : "—"} />
        </div>
      </Card>
      {data.dogman_names?.length > 0 && (
        <Card>
          <h4 className="text-sm font-bold uppercase text-slate-600 mb-2 flex items-center gap-1">
            <UserIcon className="w-4 h-4" /> Dogmen
          </h4>
          <div className="flex flex-wrap gap-2">
            {data.dogman_names.map((d, i) => (
              <Badge key={i} variant="default">{d}</Badge>
            ))}
          </div>
        </Card>
      )}
      <Card>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold uppercase text-slate-600 flex items-center gap-1">
            <Receipt className="w-4 h-4" /> Daybook Receipts ({receipts.length})
          </h4>
          <span className="text-xs text-slate-500">{fmtMoney(totalReceipts)}</span>
        </div>
        {receipts.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No receipts logged yet.</p>
        ) : (
          <div className="space-y-2">
            {receipts.map((r, i) => (
              <div
                key={r.id || i}
                className="flex items-start justify-between p-2 bg-slate-50 rounded border border-slate-200"
                data-testid={`receipt-${r.id || i}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm">{r.vendor || r.supplier || r.category || "Receipt"}</p>
                  <p className="text-xs text-slate-600">
                    {r.date ? fmtDateTime(r.date) : ""}
                    {r.category ? ` · ${r.category}` : ""}
                    {r.payment_type ? ` · ${r.payment_type}` : ""}
                  </p>
                  {r.notes && <p className="text-[11px] text-slate-500 mt-1">{r.notes}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold">{fmtMoney(r.amount)}</p>
                  {r.needs_reimbursement && (
                    <Badge variant={r.reimbursement_status === "paid" ? "success" : "warning"}>
                      {(r.reimbursement_status || "pending").toUpperCase()}
                    </Badge>
                  )}
                  {(r.receipt_url || r.photo_url) && (
                    <button
                      type="button"
                      onClick={() => onPreviewImage && onPreviewImage(r.receipt_url || r.photo_url)}
                      className="block text-xs text-blue-600 hover:underline mt-1"
                      data-testid={`receipt-view-${r.id || i}`}
                    >
                      <ExternalLink className="w-3 h-3 inline" /> View
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold uppercase text-slate-600 flex items-center gap-1">
            <FileText className="w-4 h-4" /> Daily Logs ({dailyLogs.length})
          </h4>
          <span className="text-xs text-slate-500">{totalLoggedHours.toFixed(1)}h</span>
        </div>
        {dailyLogs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No daily log entries yet.</p>
        ) : (
          <div className="space-y-2">
            {dailyLogs.map((dl, i) => (
              <div
                key={dl.id || i}
                className="p-2 bg-slate-50 rounded border border-slate-200"
                data-testid={`daily-log-${dl.id || i}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-bold text-sm">{dl.date}</p>
                  <Badge variant="info">{dl.hours}h</Badge>
                </div>
                {dl.notes && <p className="text-xs text-slate-600 mt-1">{dl.notes}</p>}
                {dl.logged_by_name && (
                  <p className="text-[11px] text-slate-400 mt-0.5">by {dl.logged_by_name}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
      {data.notes && (
        <Card>
          <h4 className="text-sm font-bold uppercase text-slate-600 mb-2">Notes</h4>
          <p className="text-sm whitespace-pre-line">{data.notes}</p>
        </Card>
      )}
    </div>
  );
};

const HireDetail = ({ data, onPreviewImage }) => {
  const receipts = data.daybook_receipts || [];
  const totalReceipts = receipts.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="hire-detail">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Operator" value={data.operator_name || "—"} />
        <Stat label="Client" value={data.client_name || "—"} />
        <Stat label="Started" value={fmtDateTime(data.clock_on_time)} />
        <Stat label="Status" value={data.status} />
      </div>
      <Card>
        <Stat label="Machine" value={`${data.machine_name || ""} ${data.machine_rego ? "(" + data.machine_rego + ")" : ""}`} />
        {data.location && <Stat label="Location" value={data.location} />}
      </Card>
      <Card>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold uppercase text-slate-600 flex items-center gap-1">
            <Receipt className="w-4 h-4" /> Receipts ({receipts.length})
          </h4>
          <span className="text-xs text-slate-500">{fmtMoney(totalReceipts)}</span>
        </div>
        {receipts.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No receipts logged yet.</p>
        ) : (
          <div className="space-y-2">
            {receipts.map((r, i) => (
              <div key={r.id || i} className="flex items-start justify-between p-2 bg-slate-50 rounded border border-slate-200">
                <div>
                  <p className="font-bold text-sm">{r.vendor || r.category}</p>
                  <p className="text-xs text-slate-600">{r.date ? fmtDateTime(r.date) : ""}</p>
                </div>
                <p className="font-bold">{fmtMoney(r.amount)}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase font-bold text-slate-500">{label}</p>
    <p className="text-sm font-medium text-slate-800 truncate">{value || "—"}</p>
  </div>
);

export default LiveActivityDetail;
